
import Vue from "vue";
import { EventBus } from "../main";
import {
  API_BASE_URL,
  CATALOG_CATEGORY_OPTIONS,
  CATALOG_OCCASIONS,
} from "../config/catalog";

const formatCatalogCount = (value: number) =>
  new Intl.NumberFormat("es-CL").format(value);

const normalizeCatalogCount = (value: unknown) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) && parsedValue >= 0 ? parsedValue : 0;
};

const SEARCH_PARAM_OCCASION = "ocasion";
const SEARCH_PARAM_QUERY = "q";
const SEARCH_PARAM_CATEGORY = "categoria";
const SEARCH_PARAM_REGION = "region";
const REGIONS_ENDPOINT_URL = API_BASE_URL ? `${API_BASE_URL}/api/regions` : "/api/regions";
const OCCASION_ALIASES: Record<string, string> = {
  dia_de_la_madre: "dia_de_las_madres",
  corporativo: "regalos_corporativos",
  regalos_coorporativos: "regalos_corporativos",
};

const slugifyValue = (value: unknown) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/&/g, " y ")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const normalizeOccasionSlug = (value: unknown) => {
  const slug = slugifyValue(value);
  return OCCASION_ALIASES[slug] || slug;
};

const OCCASION_METADATA = CATALOG_OCCASIONS.reduce(
  (acc, occasion) => ({
    ...acc,
    [occasion.value]: occasion,
  }),
  {} as Record<string, { label: string; value: string; icon: string }>
);

const normalizeText = (value: unknown) =>
  typeof value === "string" ? value.trim() : "";

const getSelectionValue = (item: unknown) => {
  if (typeof item === "object" && item !== null) {
    if ("value" in item) {
      return (item as { value?: unknown }).value;
    }

    if ("label" in item) {
      return (item as { label?: unknown }).label;
    }
  }

  return item;
};

const normalizeSelectedValues = (
  value: unknown,
  normalizeValue: (item: unknown) => string,
  allowedValues: string[] | null = null
) => {
  const rawItems = Array.isArray(value)
    ? value
    : String(value || "")
      .trim()
      .split(",");
  const normalizedItems = rawItems
    .map((item) => normalizeValue(getSelectionValue(item)))
    .filter((item) =>
      Boolean(item) && (!allowedValues || allowedValues.includes(item))
    );

  return [...new Set(normalizedItems)];
};

const normalizeSelectedOccasions = (
  value: unknown,
  allowedValues: string[] | null = null
) => normalizeSelectedValues(value, normalizeOccasionSlug, allowedValues);

const normalizeSelectedCategories = (
  value: unknown,
  allowedValues: string[] | null = null
) => normalizeSelectedValues(value, slugifyValue, allowedValues);

const normalizeSelectedRegions = (value: unknown) =>
  normalizeSelectedValues(value, (item) => String(item || "").trim());

const areSameStringArrays = (left: unknown, right: string[]) => {
  if (!Array.isArray(left) || left.length !== right.length) {
    return false;
  }

  return left.every((item, index) => item === right[index]);
};

const parseCsvParam = (value: unknown) =>
  normalizeSelectedOccasions(value);

export default Vue.extend({
  name: "CatalogToolbar",
  props: {
    focusedOccasion: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      occasions: [] as Array<{ label: string; value: string; icon: string }>,
      categoryItems: CATALOG_CATEGORY_OPTIONS.map((label) => ({
        label,
        value: slugifyValue(label),
      })),
      regionItems: [] as Array<{ label: string; value: string }>,
      searchInput: "",
      selectedOccasions: [] as string[],
      selectedCategories: [] as string[],
      selectedRegions: [] as string[],
      searchDebounceId: null as ReturnType<typeof setTimeout> | null,
      totalPymeCount: 0,
      filteredPymeCount: 0,
      hasCatalogMetrics: false,
      isSyncingFromUrl: false,
      isApplyingBulkFilterUpdate: false,
    };
  },
  computed: {
    selectedOccasionCount(): number {
      return Array.isArray(this.selectedOccasions)
        ? this.selectedOccasions.length
        : 0;
    },
    selectedCategoryCount(): number {
      return Array.isArray(this.selectedCategories)
        ? this.selectedCategories.length
        : 0;
    },
    selectedRegionCount(): number {
      return Array.isArray(this.selectedRegions)
        ? this.selectedRegions.length
        : 0;
    },
    hasActiveFilters(): boolean {
      return Boolean(
        this.selectedOccasionCount ||
        this.selectedCategoryCount ||
        this.selectedRegionCount ||
        this.searchInput.trim()
      );
    },
    activeOccasionItems(): Array<{ label: string; value: string; icon: string }> {
      return this.occasions.filter((occasion) =>
        this.selectedOccasions.includes(occasion.value)
      );
    },
    selectedCategoryItems(): Array<{ label: string; value: string }> {
      return this.categoryItems.filter(
        (category) => this.selectedCategories.includes(category.value)
      );
    },
    regionSelectItems(): Array<{ label: string; value: string }> {
      const selectedRegions = Array.isArray(this.selectedRegions)
        ? this.selectedRegions
        : [];
      const missingSelectedRegions = selectedRegions.filter(
        (selectedRegion) =>
          selectedRegion &&
          !this.regionItems.some((region) => region.value === selectedRegion)
      );

      return missingSelectedRegions.length
        ? [
          ...this.regionItems,
          ...missingSelectedRegions.map((region) => ({
            label: region,
            value: region,
          })),
        ]
        : this.regionItems;
    },
    selectedRegionItems(): Array<{ label: string; value: string }> {
      return this.regionSelectItems.filter(
        (region) => this.selectedRegions.includes(region.value)
      );
    },
    displayedPymeCount(): number {
      return this.hasActiveFilters
        ? this.filteredPymeCount
        : this.totalPymeCount;
    },
    formattedDisplayedPymeCount(): string {
      if (!this.hasCatalogMetrics) {
        return "...";
      }

      return formatCatalogCount(this.displayedPymeCount);
    },
    formattedTotalPymeCount(): string {
      if (!this.hasCatalogMetrics) {
        return "...";
      }

      return formatCatalogCount(this.totalPymeCount);
    },
    resultsCounterText(): string {
      if (!this.hasCatalogMetrics) {
        return "Cargando total...";
      }

      return this.hasActiveFilters
        ? `${this.formattedDisplayedPymeCount} ${this.displayedPymeCount === 1 ? "resultado" : "resultados"
        }`
        : `${this.formattedTotalPymeCount} pymes en total`;
    },
  },
  created() {
    this.syncFromUrl();
    EventBus.$on("catalogMetricsUpdated", this.handleCatalogMetricsUpdated);
    EventBus.$on("catalogOccasionsUpdated", this.handleCatalogOccasionsUpdated);
    this.loadRegionItems();
  },
  mounted() {
    window.addEventListener("popstate", this.handlePopState);
    this.emitCatalogFilters();
  },
  beforeDestroy() {
    window.removeEventListener("popstate", this.handlePopState);
    EventBus.$off("catalogMetricsUpdated", this.handleCatalogMetricsUpdated);
    EventBus.$off("catalogOccasionsUpdated", this.handleCatalogOccasionsUpdated);

    if (this.searchDebounceId) {
      clearTimeout(this.searchDebounceId);
      this.searchDebounceId = null;
    }
  },
  methods: {
    handleCatalogMetricsUpdated(payload: {
      totalCount?: number;
      filteredCount?: number;
    }) {
      this.totalPymeCount = normalizeCatalogCount(payload?.totalCount);
      this.filteredPymeCount = normalizeCatalogCount(payload?.filteredCount);
      this.hasCatalogMetrics = true;
    },
    handleCatalogOccasionsUpdated(
      payload: Array<
        { label?: string; name?: string; value?: string; emoji?: string } | string
      >
    ) {
      const rawItems = Array.isArray(payload) ? payload : [];
      const nextOccasions = rawItems.reduce(
        (
          items: Array<{ label: string; value: string; icon: string }>,
          item
        ) => {
          const rawLabel =
            typeof item === "string" ? item : item?.label || item?.name || "";
          const rawValue =
            typeof item === "string" ? item : item?.value || rawLabel;
          const rawEmoji = typeof item === "string" ? "" : item?.emoji;
          const value = normalizeOccasionSlug(rawValue);
          const label = String(rawLabel || "").trim();

          if (!value || !label || items.some((occasion) => occasion.value === value)) {
            return items;
          }

          const metadata = OCCASION_METADATA[value];

          return [
            ...items,
            {
              label,
              value,
              icon: normalizeText(rawEmoji) || metadata?.icon || "",
            },
          ];
        },
        []
      );

      this.occasions = nextOccasions.sort((left, right) =>
        left.label.localeCompare(right.label, "es-CL")
      );
      this.normalizeSelectedOccasionsAgainstOptions(true);
    },
    normalizeSelectedOccasionsAgainstOptions(replaceUrl = false) {
      const availableValues = this.occasions.map((occasion) => occasion.value);
      const normalizedOccasions = normalizeSelectedOccasions(
        this.selectedOccasions,
        availableValues
      );

      if (areSameStringArrays(this.selectedOccasions, normalizedOccasions)) {
        return;
      }

      this.isApplyingBulkFilterUpdate = true;
      this.selectedOccasions = normalizedOccasions;
      this.$nextTick(() => {
        this.isApplyingBulkFilterUpdate = false;

        if (replaceUrl) {
          this.syncUrl(true);
          return;
        }

        this.emitCatalogFilters();
      });
    },
    async loadRegionItems() {
      try {
        const response = await fetch(REGIONS_ENDPOINT_URL);

        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }

        const payload = await response.json();
        const rawItems = Array.isArray(payload) ? payload : [];
        const nextRegionItems = [...new Set(
          rawItems
            .map((region) =>
              typeof region?.name === "string" ? region.name.trim() : ""
            )
            .filter(Boolean)
        )]
          .sort((left, right) => left.localeCompare(right, "es-CL"))
          .map((region) => ({
            label: region,
            value: region,
          }));

        this.regionItems = nextRegionItems;
      } catch (error) {
        console.error("Error loading catalog regions", error);
      }
    },
    syncFromUrl() {
      if (typeof window === "undefined") {
        return;
      }

      this.isSyncingFromUrl = true;
      const url = new URL(window.location.href);
      const urlOccasions = parseCsvParam(
        url.searchParams.get(SEARCH_PARAM_OCCASION)
      );
      const focusedOccasionValue = this.getFocusedOccasionValue();
      this.selectedOccasions = urlOccasions.length
        ? urlOccasions
        : focusedOccasionValue
          ? [focusedOccasionValue]
          : [];
      this.searchInput = String(url.searchParams.get(SEARCH_PARAM_QUERY) || "").trim();
      this.selectedCategories = normalizeSelectedCategories(
        url.searchParams.get(SEARCH_PARAM_CATEGORY),
        this.categoryItems.map((category) => category.value)
      );
      this.selectedRegions = normalizeSelectedRegions(
        url.searchParams.get(SEARCH_PARAM_REGION)
      );
      this.$nextTick(() => {
        this.isSyncingFromUrl = false;
      });
    },
    getFocusedOccasionValue(): string {
      return normalizeSelectedOccasions([this.focusedOccasion])[0] || "";
    },
    applyFocusedOccasion() {
      if (typeof window === "undefined") {
        return;
      }

      const focusedOccasionValue = this.getFocusedOccasionValue();

      if (!focusedOccasionValue || this.selectedOccasions.length) {
        return;
      }

      const url = new URL(window.location.href);

      if (url.searchParams.has(SEARCH_PARAM_OCCASION)) {
        return;
      }

      this.selectedOccasions = [focusedOccasionValue];
    },
    buildCatalogUrl() {
      const currentUrl = new URL(window.location.href);
      const nextUrl = new URL(currentUrl.origin);

      nextUrl.pathname = currentUrl.pathname || "/";

      if (this.selectedOccasions.length) {
        nextUrl.searchParams.set(
          SEARCH_PARAM_OCCASION,
          this.selectedOccasions.join(",")
        );
      }

      if (this.searchInput.trim()) {
        nextUrl.searchParams.set(SEARCH_PARAM_QUERY, this.searchInput.trim());
      }

      if (this.selectedCategories.length) {
        nextUrl.searchParams.set(
          SEARCH_PARAM_CATEGORY,
          this.selectedCategories.join(",")
        );
      }

      if (this.selectedRegions.length) {
        nextUrl.searchParams.set(
          SEARCH_PARAM_REGION,
          this.selectedRegions.join(",")
        );
      }

      return nextUrl.toString();
    },
    emitCatalogFilters() {
      EventBus.$emit("catalogFiltersChanged", {
        occasions: [...this.selectedOccasions],
        query: this.searchInput.trim(),
        categories: this.selectedCategoryItems.map((category) => category.label),
        regions: [...this.selectedRegions],
      });
      this.$emit("filters-state-change", this.hasActiveFilters);
    },
    syncUrl(replace = false) {
      if (typeof window === "undefined") {
        return;
      }

      const nextUrl = this.buildCatalogUrl();

      if (replace) {
        window.history.replaceState({}, "", nextUrl);
      } else {
        window.history.pushState({}, "", nextUrl);
      }

      this.emitCatalogFilters();
    },
    removeOccasion(value: string) {
      this.selectedOccasions = this.selectedOccasions.filter(
        (occasion) => occasion !== value
      );
      this.syncUrl();
    },
    clearOccasions() {
      this.selectedOccasions = [];
    },
    removeCategory(value: string) {
      this.selectedCategories = this.selectedCategories.filter(
        (category) => category !== value
      );
      this.syncUrl();
    },
    removeRegion(value: string) {
      this.selectedRegions = this.selectedRegions.filter(
        (region) => region !== value
      );
      this.syncUrl();
    },
    handleCategoryChange(value: unknown) {
      this.selectedCategories = normalizeSelectedCategories(
        value,
        this.categoryItems.map((category) => category.value)
      );
      this.syncUrl();
    },
    handleRegionChange(value: unknown) {
      this.selectedRegions = normalizeSelectedRegions(value);
      this.syncUrl();
    },
    clearCategories() {
      this.selectedCategories = [];
      this.syncUrl();
    },
    clearRegions() {
      this.selectedRegions = [];
      this.syncUrl();
    },
    clearSearch() {
      this.searchInput = "";
      this.syncUrl(true);
    },
    handleSearchInput() {
      if (this.searchDebounceId) {
        clearTimeout(this.searchDebounceId);
      }

      this.searchDebounceId = setTimeout(() => {
        this.syncUrl(true);
        this.searchDebounceId = null;
      }, 250);
    },
    clearAllFilters() {
      this.isApplyingBulkFilterUpdate = true;
      this.selectedOccasions = [];
      this.selectedCategories = [];
      this.selectedRegions = [];
      this.searchInput = "";
      this.$nextTick(() => {
        this.isApplyingBulkFilterUpdate = false;
        this.syncUrl();
      });
    },
    handlePopState() {
      this.syncFromUrl();
      this.emitCatalogFilters();
    },
  },
  watch: {
    focusedOccasion() {
      this.applyFocusedOccasion();
    },
    selectedOccasions: {
      handler(value: unknown) {
        const availableValues = this.occasions.map((occasion) => occasion.value);
        const normalizedOccasions = normalizeSelectedOccasions(
          value,
          availableValues.length ? availableValues : null
        );

        if (!areSameStringArrays(value, normalizedOccasions)) {
          this.selectedOccasions = normalizedOccasions;
          return;
        }

        if (this.isSyncingFromUrl || this.isApplyingBulkFilterUpdate) {
          return;
        }

        this.syncUrl();
      },
      deep: true,
    },
  },
});
